import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout/Layout';
import './AdminUserManagement.css';

const AdminUserManagement = () => {
  const { user, getAuthHeaders } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    role: 'admin',
    is_active: true
  });

  // Redirect if not main admin
  useEffect(() => {
    if (user && user.username !== 'admin') {
      window.location.href = '/';
      return;
    }
  }, [user]);

  // Load users
  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(''); // Clear previous errors
      const response = await fetch('/api/admin-users', {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setUsers(data.data.users);
        setError(''); // Clear any previous errors on success
      } else {
        // Handle authentication errors
        if (response.status === 401 || data.code === 'TOKEN_EXPIRED' || data.code === 'INVALID_TOKEN' || data.code === 'TOKEN_VERIFICATION_FAILED') {
          // Clear auth data and redirect to login
          localStorage.removeItem('travel-futura-token');
          localStorage.removeItem('travel-futura-user');
          window.location.href = '/login';
          return;
        }
        setError(data.message || 'Failed to load users');
      }
    } catch (error) {
      console.error('Load users error:', error);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.username === 'admin') {
      loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Handle form changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      full_name: '',
      role: 'admin',
      is_active: true
    });
    setEditingUser(null);
    setShowAddModal(false);
  };

  // Handle create user
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Client-side validation
    if (!formData.username || formData.username.trim() === '') {
      setError('Username is required');
      return;
    }

    // Validate username format (letters, numbers, hyphens, underscores only)
    const usernameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!usernameRegex.test(formData.username)) {
      setError('Username can only contain letters, numbers, hyphens (-), and underscores (_). No spaces or special characters allowed.');
      return;
    }

    if (formData.username.length < 3 || formData.username.length > 50) {
      setError('Username must be between 3 and 50 characters');
      return;
    }

    if (!formData.email || formData.email.trim() === '') {
      setError('Email is required');
      return;
    }

    if (!formData.password || formData.password.trim() === '') {
      setError('Password is required');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      const response = await fetch('/api/admin-users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('User created successfully!');
        resetForm();
        loadUsers();
      } else {
        // Handle authentication errors
        if (response.status === 401 || data.code === 'TOKEN_EXPIRED' || data.code === 'INVALID_TOKEN' || data.code === 'TOKEN_VERIFICATION_FAILED') {
          // Clear auth data and redirect to login
          localStorage.removeItem('travel-futura-token');
          localStorage.removeItem('travel-futura-user');
          window.location.href = '/login';
          return;
        }
        setError(data.message || 'Failed to create user');
      }
    } catch (error) {
      console.error('Create user error:', error);
      setError('Failed to create user. Please try again.');
    }
  };

  // Handle edit user
  const handleEditClick = (userToEdit) => {
    setEditingUser(userToEdit);
    setFormData({
      username: userToEdit.username,
      email: userToEdit.email,
      password: '',
      full_name: userToEdit.full_name || '',
      role: userToEdit.role,
      is_active: userToEdit.is_active
    });
    setShowAddModal(true);
  };

  // Handle update user
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate username format if it's being changed
    if (formData.username && formData.username !== editingUser?.username) {
      const usernameRegex = /^[a-zA-Z0-9_-]+$/;
      if (!usernameRegex.test(formData.username)) {
        setError('Username can only contain letters, numbers, hyphens (-), and underscores (_). No spaces or special characters allowed.');
        return;
      }

      if (formData.username.length < 3 || formData.username.length > 50) {
        setError('Username must be between 3 and 50 characters');
        return;
      }
    }

    try {
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password; // Don't send empty password
      }

      const response = await fetch(`/api/admin-users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(updateData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('User updated successfully!');
        resetForm();
        loadUsers();
      } else {
        // Handle authentication errors
        if (response.status === 401 || data.code === 'TOKEN_EXPIRED' || data.code === 'INVALID_TOKEN' || data.code === 'TOKEN_VERIFICATION_FAILED') {
          // Clear auth data and redirect to login
          localStorage.removeItem('travel-futura-token');
          localStorage.removeItem('travel-futura-user');
          window.location.href = '/login';
          return;
        }
        setError(data.message || 'Failed to update user');
      }
    } catch (error) {
      console.error('Update user error:', error);
      setError('Failed to update user. Please try again.');
    }
  };

  // Handle delete user
  const handleDeleteUser = async (userToDelete) => {
    if (userToDelete.username === 'admin') {
      setError('Cannot delete the main admin user');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete user "${userToDelete.username}"?`)) {
      return;
    }

    try {
      setError('');
      const response = await fetch(`/api/admin-users/${userToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('User deleted successfully!');
        loadUsers();
      } else {
        // Handle authentication errors
        if (response.status === 401 || data.code === 'TOKEN_EXPIRED' || data.code === 'INVALID_TOKEN' || data.code === 'TOKEN_VERIFICATION_FAILED') {
          // Clear auth data and redirect to login
          localStorage.removeItem('travel-futura-token');
          localStorage.removeItem('travel-futura-user');
          window.location.href = '/login';
          return;
        }
        setError(data.message || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Delete user error:', error);
      setError('Failed to delete user. Please try again.');
    }
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'super_admin': return 'role-super-admin';
      case 'admin': return 'role-admin';
      case 'editor': return 'role-editor';
      default: return 'role-default';
    }
  };

  if (user?.username !== 'admin') {
    return (
      <Layout title="Access Denied">
        <div className="access-denied">
          <div className="access-denied-content">
            <h2>🚫 Access Denied</h2>
            <p>Only the main administrator can access user management.</p>
            <button onClick={() => window.location.href = '/'} className="btn-primary">
              Return to Dashboard
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="User Management">
      <div className="user-management">
        <div className="page-header">
          <div className="header-content">
            <h1>👥 User Management</h1>
            <p>Manage admin users and their permissions</p>
          </div>
          <button 
            className="btn-primary"
            onClick={() => setShowAddModal(true)}
          >
            ➕ Add New User
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            <span className="alert-icon">✅</span>
            {success}
          </div>
        )}

        {/* Users Table */}
        <div className="users-table-container">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading users...</p>
            </div>
          ) : (
            <table className="users-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id} className={!user.is_active ? 'inactive-user' : ''}>
                    <td>
                      <div className="user-info">
                        <div className="user-avatar">
                          {user.username.charAt(0).toUpperCase()}
                        </div>
                        <div className="user-details">
                          <div className="username">
                            {user.username}
                            {user.username === 'admin' && (
                              <span className="main-admin-badge">Main Admin</span>
                            )}
                          </div>
                          <div className="user-email">{user.email}</div>
                          {user.full_name && (
                            <div className="user-full-name">{user.full_name}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                        {user.role.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${user.is_active ? 'status-active' : 'status-inactive'}`}>
                        {user.is_active ? '✅ Active' : '❌ Inactive'}
                      </span>
                    </td>
                    <td>
                      {user.last_login ? 
                        new Date(user.last_login).toLocaleDateString() : 
                        'Never'
                      }
                    </td>
                    <td>
                      {new Date(user.created_at || user.createdAt).toLocaleDateString()}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-edit"
                          onClick={() => handleEditClick(user)}
                          title="Edit User"
                        >
                          ✏️
                        </button>
                        {user.username !== 'admin' && (
                          <button
                            className="btn-delete"
                            onClick={() => handleDeleteUser(user)}
                            title="Delete User"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Add/Edit User Modal */}
        {showAddModal && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h2>{editingUser ? '✏️ Edit User' : '➕ Add New User'}</h2>
                <button className="modal-close" onClick={resetForm}>
                  ✕
                </button>
              </div>

              <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser}>
                <div className="modal-body">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="username">Username</label>
                      <input
                        type="text"
                        id="username"
                        name="username"
                        value={formData.username}
                        onChange={handleInputChange}
                        required
                        disabled={editingUser?.username === 'admin'}
                        className={editingUser?.username === 'admin' ? 'disabled' : ''}
                      />
                      {editingUser?.username === 'admin' ? (
                        <small className="form-help">Main admin username cannot be changed</small>
                      ) : (
                        <small className="form-help">3-50 characters. Only letters, numbers, hyphens (-), and underscores (_). No spaces or special characters.</small>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="email">Email</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="password">
                        Password {editingUser && <small>(leave blank to keep current)</small>}
                      </label>
                      <input
                        type="password"
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        required={!editingUser}
                        minLength="6"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="full_name">Full Name</label>
                      <input
                        type="text"
                        id="full_name"
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="role">Role</label>
                      <select
                        id="role"
                        name="role"
                        value={formData.role}
                        onChange={handleInputChange}
                        disabled={editingUser?.username === 'admin'}
                        className={editingUser?.username === 'admin' ? 'disabled' : ''}
                      >
                        <option value="editor">Editor</option>
                        <option value="admin">Admin</option>
                        <option value="super_admin">Super Admin</option>
                      </select>
                      {editingUser?.username === 'admin' && (
                        <small className="form-help">Main admin role cannot be changed</small>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          name="is_active"
                          checked={formData.is_active}
                          onChange={handleInputChange}
                        />
                        <span className="checkbox-custom"></span>
                        Active User
                      </label>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn-secondary" onClick={resetForm}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingUser ? 'Update User' : 'Create User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminUserManagement;
